import * as React from 'react';
import * as moment from 'moment';
import { EditState, EditProps, BaseRouteProps } from '../../interfaces';
import { Team, Organization, Language, User, SurveyInstance } from '../../models';
import { OrganizationDataService, UserDataService, LanguageDataService, SurveyInstanceDataService } from '../../services';
import { NavLink } from 'react-router-dom';
import { UserForm } from './UserForm';
import { FormState } from 'react-form';
import { LoadingIndicator } from '../../components';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

interface UserEditState extends EditState<User> {
    organizations: Organization[];
    teams: Team[];
    languages: Language[];
    users: User[];
    surveyInvites: SurveyInstance[];
    previousOrganizationId: number;
    loading: boolean;
}

interface UserEditProps extends BaseRouteProps<EditProps> {
    UserStore?: UserStore;
}

@inject('UserStore') @observer
export class UserEdit extends React.Component<UserEditProps, UserEditState> {
    private organizationDataService = new OrganizationDataService(this.props.notificationService);
    private languageDataService = new LanguageDataService(this.props.notificationService);
    private userDataService = new UserDataService(this.props.notificationService);
    private surveyInstanceService = new SurveyInstanceDataService(this.props.notificationService);

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get organization() {
        return this.props.UserStore!.userInfo.organization;
    }

    @computed get userCanSeeInviteDetails() {
        return this.props.UserStore!.checkUserPermission('CanViewSurveyResults') || this.props.UserStore!.userRoleIn(['superAdmin', 'siteAdmin', 'ponderaAssociate']);
    }

    constructor(props: UserEditProps) {
        super(props);

        this.state = {
            originalEntity: undefined,
            organizations: new Array<Organization>(),
            teams: new Array<Team>(),
            languages: new Array<Language>(),
            users: new Array<User>(),
            surveyInvites: new Array<SurveyInstance>(),
            previousOrganizationId: 0,
            loading: false
        };

        this.save = this.save.bind(this);
        this.handleFormUpdate = this.handleFormUpdate.bind(this);
    }

    public componentDidMount() {
        const id = Number(this.props.match.params.id);

        if (['organizationAdmin', 'manager', 'user'].includes(this.userRole)) {
            this.userDataService.get(id!).then((user: User) => {
                this.userDataService.getByOrganizationLight(user.organization.id).then((users: User[]) => {
                    const potentialManagers = users.filter(u => u.id !== user.id);

                    this.setState({ originalEntity: user, previousOrganizationId: user.organization.id, users: potentialManagers });
                });
            });

            this.organizationDataService.get(this.organization!.id).then((organization: Organization) => {
                this.setState({ organizations: [organization] });
            });

            this.organizationDataService.getTeamsForOrganization(this.organization!.id).then((teams: Team[]) => {
                this.setState({ teams: teams });
            });
        } else {
            this.userDataService.get(id!).then((user: User) => {
                this.setState({ originalEntity: user, previousOrganizationId: user.organization.id });

                this.userDataService.getByOrganizationLight(user.organization.id).then((users: User[]) => {
                    const potentialManagers = users.filter(u => u.id !== id!);

                    this.setState({ users: potentialManagers });
                });

                this.organizationDataService.getTeamsForOrganization(user.organization.id).then((teams: Team[]) => {
                    this.setState({ teams: teams });
                });
            });

            this.organizationDataService.getAll().then((companies: Organization[]) => {
                this.setState({ organizations: companies });
            });
        }

        this.surveyInstanceService.getAllForUser(id).then((surveyInstances: SurveyInstance[]) => {
            this.setState({ surveyInvites: surveyInstances });
        });

        this.languageDataService.getAll().then((languages: Language[]) => {
            this.setState({ languages: languages });
        });
    }

    private handleFormUpdate(formState: FormState) {
        if (formState.values.organizationID !== this.state.previousOrganizationId) {
            this.setState({ previousOrganizationId: formState.values.organizationID });
            this.organizationDataService.getTeamsForOrganization(formState.values.organizationID).then((teams: Team[]) => {
                this.setState({ teams: teams });
            });

            const organization = this.state.organizations.find(o => {
                return o.id === formState.values.organizationID;
            });

            const id = Number(this.props.match.params.id);
            this.userDataService.getByOrganizationLight(organization!.id).then((users: User[]) => {
                const potentialManagers = users.filter(u => u.id !== id!);

                this.setState({ users: potentialManagers });
            });
        }
    }

    private save(values: any) {
        this.userDataService.update(values)
            .then((success) => {
                this.props.history.push('/user');
            })
            .catch(() => {
                this.props.notificationService.addError('Please address validation messages and try again.');
            });
    }

    private sendPasswordResetEmail() {
        const confirmed = confirm(`Are you sure you want to send a reset password email to ${this.state.originalEntity!.firstName} ${this.state.originalEntity!.lastName}?`);

        if (confirmed && this.props.match.params.id) {
            this.userDataService.sendPasswordReset(this.props.match.params.id).then(
                () => {
                    this.props.notificationService.addNotification('Reset Password Email Sent Successfully.');
                }
            );
        }
    }

    private async bypassMfa() {
        this.setState({ loading: true });
        if (this.props.match.params.id) {
            await this.userDataService.bypassMfa(this.props.match.params.id);
            this.props.notificationService.addNotification('MFA Bypassed Successfully.');
            this.reloadUser();
        }
    }

    private reloadUser() {
        this.setState({ loading: true });
        this.userDataService.get(this.props.match.params.id!).then((user: User) => {
            this.setState({ originalEntity: user, previousOrganizationId: user.organization.id });

            this.userDataService.getByOrganizationLight(user.organization.id).then((users: User[]) => {
                const potentialManagers = users.filter(u => u.id !== this.props.match.params.id!);

                this.setState({ users: potentialManagers });
            });

            this.organizationDataService.getTeamsForOrganization(user.organization.id).then((teams: Team[]) => {
                this.setState({ teams: teams });
            });
        });
        this.setState({ loading: false });
    }

    private createPersonalAccount() {
        const confirmed = confirm(`Are you sure you want to create a personal login for ${this.state.originalEntity!.firstName} ${this.state.originalEntity!.lastName}?`);

        if (confirmed && this.props.match.params.id) {
            this.setState({ loading: true });
            this.userDataService.createPersonalAccount(this.props.match.params.id).then(
                () => {
                    this.props.notificationService.addNotification('Personal account created successfully.');
                    this.reloadUser();
                },
                () => {
                    this.props.notificationService.addError('Error creating personal account.');
                    this.setState({ loading: false });
                }
            );
        }
    }

    private deactivateUser() {
        const confirmed = confirm(`Are you sure you want to deactivate ${this.state.originalEntity!.firstName} ${this.state.originalEntity!.lastName}? This will prevent them from accessing the system, but will continue to include their data within reports.`);

        if (confirmed && this.props.match.params.id) {
            this.userDataService.deactivate(this.props.match.params.id).then(
                () => {
                    this.setState({ originalEntity: Object.assign({}, this.state.originalEntity, { isDeactivated: true }) });
                }
            );
        }
    }

    private reactivateUser() {
        const confirmed = confirm(`Are you sure you want to reactivate ${this.state.originalEntity!.firstName} ${this.state.originalEntity!.lastName}?`);

        if (confirmed && this.props.match.params.id) {
            this.userDataService.reactivate(this.props.match.params.id).then(
                () => {
                    this.setState({ originalEntity: Object.assign({}, this.state.originalEntity, { isDeactivated: false }) });
                }
            );
        }
    }

    private deleteUser() {
        const confirmed = confirm(`Are you sure you want to delete ${this.state.originalEntity!.firstName} ${this.state.originalEntity!.lastName}? This will prevent them from accessing the system and also remove their data from reports.`);

        if (confirmed && this.props.match.params.id) {
            this.userDataService.delete(this.state.originalEntity!).then(
                () => {
                    this.props.history.push('/user');
                }
            );
        }
    }

    public render() {
        const { originalEntity, surveyInvites } = this.state;
        if (!originalEntity || this.state.loading) {
            return <LoadingIndicator />;
        }

        return (
            <div>
                <div className="content-header">
                    <div className="content-container">
                        <div className="title">
                            <NavLink to={`/user`}>
                                <span className="glyphicon glyphicon-triangle-left" />
                            </NavLink>
                            &nbsp;
                            Edit User
                        </div>
                    </div>
                </div>

                <div className="content-body">
                    <div className="content-container">
                        <UserForm defaultValues={originalEntity} submit={this.save} {...this.state} notificationService={this.props.notificationService} />
                        <hr />
                        {originalEntity!.needsToPassMfa && ['superAdmin','siteAdmin'].includes(this.userRole) &&
                            <button className="btn btn-default" onClick={(e) => this.bypassMfa()}> ⏭ Bypass MFA</button>
                        }
                        {!originalEntity!.welcomeSent &&
                            <button className="btn btn-default" onClick={(e) => this.createPersonalAccount()}><span className="glyphicon glyphicon-user" /> Create Personal Login</button>
                        }
                        &nbsp;&nbsp;
                        {originalEntity!.welcomeSent &&
                            <button className="btn btn-default" onClick={(e) => this.sendPasswordResetEmail()}><span className="glyphicon glyphicon-envelope"/> Send Reset Password Email</button>
                        }
                        &nbsp;&nbsp;
                        {originalEntity!.isDeactivated &&
                            <button className="btn btn-success" onClick={(e) => this.reactivateUser()}><span className="glyphicon glyphicon-play" /> Reactivate</button>
                        }
                        {!originalEntity!.isDeactivated &&
                            <button className="btn btn-warning" onClick={(e) => this.deactivateUser()}><span className="glyphicon glyphicon-pause" /> Deactivate</button>
                        }
                        &nbsp;&nbsp;
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) && <button className="btn btn-danger" onClick={(e) => this.deleteUser()}><span className="glyphicon glyphicon-remove" /> Delete</button>}
                        <hr/>
                        <h3>Survey Invites</h3>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Survey Key</th>
                                    <th>Completed</th>
                                    <th>Expires</th>
                                    <th>Active</th>
                                    <th/>
                                </tr>
                            </thead>
                            <tbody>
                                {surveyInvites.map((invite, index) => (
                                    <tr key={`invite-${invite.id}`}>
                                        <td>
                                            {invite.uniqueIdentifier}
                                        </td>
                                        <td>
                                            {invite.completedDate != null && <span><span className="glyphicon glyphicon-ok" />&nbsp;&nbsp;{moment(invite.completedDate).toDate().toDateString()}</span>}
                                            {invite.completedDate == null && <span className="glyphicon glyphicon-remove" />}
                                        </td>
                                        <td>
                                            {invite.completedDate == null && <span>{moment(invite.expirationDate).toDate().toDateString()}</span>} 
                                        </td>
                                        <td>
                                            {invite.active && <span>Active</span>} 
                                        </td>
                                        <td>
                                            {this.userCanSeeInviteDetails && <NavLink className="btn btn-default" to={`/surveyinvite/${invite.uniqueIdentifier}`}>View</NavLink>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>);
    }
}


// WEBPACK FOOTER //
// ./src/components/user/UserEdit.tsx